import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { BodyText, Button, Modal, theme, useResponsive } from '@sg/garnish';

import { CustomizationModalContentContainer } from '../CustomizationModalContentContainer';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationRequiredModificationModal = (props: Props) => {
  const { isVisible, productName, requiredIngredientName, onConfirm } = props;

  const { formatMessage } = useIntl();
  const { match } = useResponsive();

  // ─────────────────────────────────────────────────────────────────────

  const ingredientName = (
    requiredIngredientName ?? formatMessage(messages.ingredient)
  ).toLowerCase();

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Modal
      visible={isVisible}
      size="small"
      accessibilityLabel={formatMessage(messages.modalLabel)}
      onRequestClose={onConfirm}
    >
      <CustomizationModalContentContainer size={match(['md', 'lg'])}>
        <BodyText sizeMatch={['22']}>
          <FormattedMessage
            {...messages.body}
            values={{ ingredient: ingredientName, product: productName }}
          />
        </BodyText>

        <View style={styles.ctaContainer}>
          <Button
            size="large-wide"
            palette="spinachMuted"
            style={styles.cta}
            onPress={onConfirm}
          >
            <FormattedMessage {...messages.gotIt} />
          </Button>
        </View>
      </CustomizationModalContentContainer>
    </Modal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  modalLabel: {
    defaultMessage: 'Required ingredient modal',
    description: 'Customization | Required modification modal | Label',
  },
  body: {
    defaultMessage: 'This {ingredient} cannot be removed from the {product}.',
    description: 'Customization | Required modification modal | Body',
  },
  ingredient: {
    defaultMessage: 'ingredient',
    description: 'Customization | Required modification modal | Ingredient',
  },
  product: {
    defaultMessage: 'product',
    description: 'Customization | Required modification modal | Product',
  },
  gotIt: {
    defaultMessage: 'Got it',
    description:
      'Customization | Required modification modal | Got it button label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  ctaContainer: {
    alignItems: 'center',
    paddingTop: theme.spacing['2'],
  },
  cta: {
    paddingHorizontal: theme.spacing['16'],
    borderWidth: 1,
    borderColor: theme.colors.KALE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isVisible: boolean;
  productName: string;
  requiredIngredientName: string | undefined;
  onConfirm: () => void;
};
